/**
 * Korean locale for extensions-ui.
 * Export is the "extensions" subtree: host apps merge this into their i18n
 * resources as translation.extensions so that t('extensions.list.heading') etc. resolve.
 */
import type { ExtensionsLocale } from './en'

export const extensionsKo = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `취소`,
    delete: `삭제`,
    save: `저장`,
    apply: `적용`,
    edit: `편집`,
    actions: `작업`,
    add: `추가`,
    all: `전체`,
    loading: `로딩 중...`,
    noChange: `변경 없음`,
    none: `없음`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    pageLabel: `모든 내선`,
    heading: `내선`,
    linesHeading: `회선`,
    searchPlaceholder: `이름 또는 번호로 검색`,
    linesSearchPlaceholder: `이름, 번호, 할당된 프로필 또는 위치로 검색`,
    loadingExtensions: `내선 로딩 중...`,
    noMatch: `검색과 일치하는 내선이 없습니다`,
    noExtensions: `내선을 찾을 수 없습니다`,
    noMatchHint: `다른 검색어를 시도해 보세요.`,
    noExtensionsHint: `이 PBX에 내선이 없습니다.`,
    activateAll: `전체 활성화`,
    deactivateAll: `전체 비활성화`,
    massEdit: `일괄 편집`,
    linesSelected: `{{count}}개 회선 선택됨`,
  },

  // ---------------------------------------------------------------------------
  // Column headers
  // ---------------------------------------------------------------------------
  columns: {
    number: `번호`,
    name: `이름`,
    type: `유형`,
    created: `생성일`,
    updated: `수정일`,
    lastUpdated: `최근 수정`,
    assignedUser: `할당된 사용자`,
    assignedProfile: `할당된 프로필`,
    location: `위치`,
    actions: `작업`,
  },

  // ---------------------------------------------------------------------------
  // Row actions & badges
  // ---------------------------------------------------------------------------
  row: {
    inactive: `비활성`,
    edit: `편집`,
    delete: `삭제`,
    viewCallMetrics: `통화 지표 보기`,
    activate: `활성화`,
    deactivate: `비활성화`,
    extensionFallback: `내선 {{id}}`,
    selectAllLines: `모든 회선 선택`,
    selectLine: `{{name}} 선택`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `내선 삭제`,
    confirmation: `"{{displayName}}"을(를) 삭제하시겠습니까? 이 작업은 취소할 수 없습니다.`,
    unnamed: `이름 없는 내선`,
    reassignLabel: `참조 재할당 대상`,
    deleteWithoutReassigning: `재할당 없이 삭제`,
  },

  // ---------------------------------------------------------------------------
  // Set active state dialog (single)
  // ---------------------------------------------------------------------------
  activeStateDialog: {
    activateTitle: `내선 활성화`,
    deactivateTitle: `내선 비활성화`,
    body: `{{displayName}}을(를) {{action}}하려고 합니다. 이 내선을 기본 회선 내선으로 사용하는 모든 장치가 재부팅됩니다.`,
    unnamedLine: `이름 없는 회선`,
    confirmPrompt: `계속하시겠습니까?`,
    setActive: `활성화 설정`,
    setInactive: `비활성화 설정`,
  },

  // ---------------------------------------------------------------------------
  // Bulk active state dialog
  // ---------------------------------------------------------------------------
  bulkActiveDialog: {
    activateTitle: `모든 내선 활성화`,
    deactivateTitle: `모든 내선 비활성화`,
    body: `모든 내선을 {{action}}하려고 합니다. 이 내선들을 기본 회선 내선으로 사용하는 모든 장치가 재부팅됩니다.`,
    confirmPrompt: `계속하시겠습니까?`,
    activateAll: `전체 활성화`,
    deactivateAll: `전체 비활성화`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `회선 일괄 편집`,
    description: `{{count}}개 회선의 다음 필드를 업데이트합니다. 현재 값을 유지하려면 "변경 없음"으로 두세요.`,
    outboundCallerId: `발신자 ID`,
    localPresence: `로컬 프레즌스`,
    preferredCodec: `선호 코덱`,
    location: `위치`,
    locationPlaceholder: `변경 없음 또는 검색어 입력`,
    locationSearchLabel: `위치 검색`,
    loadingLocations: `위치 로딩 중\u2026`,
    noLocationsFound: `위치를 찾을 수 없습니다`,
    typeToSearchLocations: `위치를 검색하려면 입력하세요`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `페이지당 결과:`,
    previousPage: `이전 페이지`,
    nextPage: `다음 페이지`,
    all: `전체`,
    jumpToPage: `{{page}}페이지로 이동`,
    searchExtensions: `내선 검색`,
    toggleFilters: `필터 전환`,
    showingAll: `전체 {{total}}개 결과 표시`,
    showingRange: `{{start}}–{{end}} / 총 {{total}}개 결과`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deleteSuccess: `내선이 삭제되었습니다.`,
    deleteFailed: `삭제 실패`,
    deleteFailedDescription: `내선을 삭제하지 못했습니다. 다시 시도해 주세요.`,
    success: `성공`,
    failed: `실패`,
    activeStateSuccess: `회선이 {{state}}(으)로 설정되었습니다.`,
    activeStateFailed: `회선 활성 상태를 업데이트하지 못했습니다.`,
    bulkSuccess: `{{count}}개 회선이 {{state}}(으)로 설정되었습니다.`,
    partialFailure: `부분 실패`,
    partialFailureDescription: `{{succeeded}}개 성공, {{failed}}개 실패. 일부 회선이 업데이트되지 않았을 수 있습니다.`,
    massEditSuccess: `{{count}}개 회선이 업데이트되었습니다.`,
    massEditFailed: `일괄 편집 실패`,
    massEditFailedDescription: `선택한 회선을 업데이트하지 못했습니다. 다시 시도해 주세요.`,
  },

  // ---------------------------------------------------------------------------
  // Extension type labels
  // ---------------------------------------------------------------------------
  types: {
    line: `회선`,
    ringGroup: `링 그룹`,
    virtualFax: `가상 팩스`,
    conferenceRoom: `회의실`,
    switchboard: `스위치보드`,
    sharedVoicemail: `공유 음성사서함`,
    callQueue: `콜 큐`,
  },

  // ---------------------------------------------------------------------------
  // View mode labels
  // ---------------------------------------------------------------------------
  viewModes: {
    extensions: `내선`,
    lines: `회선`,
    ringGroups: `링 그룹`,
    callQueues: `콜 큐`,
    conferenceRooms: `회의실`,
    virtualFax: `가상 팩스`,
    sharedVoicemail: `공유 음성사서함`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    title: `필터`,
    status: `상태:`,
    assignedUser: `할당된 사용자:`,
    assignedProfile: `할당된 프로필:`,
    location: `위치:`,
    active: `활성`,
    inactive: `비활성`,
    all: `전체`,
    clearFilters: `필터 지우기`,
    apply: `적용`,
  },

  placeholder: {
    notImplemented: `{{typeLabel}} 편집 페이지는 아직 구현되지 않았습니다.`,
    backToList: `{{listLabel}}로 돌아가기`,
  },

  edit: {
    validationError: `유효성 검사 오류`,
    reviewRequiredFields: `필수 필드를 확인한 후 다시 시도해 주세요.`,
    emailFormatInvalid: `이메일 형식이 올바르지 않습니다. 공백이 없는지 확인하세요. 여러 주소는 쉼표로 구분하세요.`,
    nameNumberRequired: `이름과 번호는 필수입니다.`,
    reservedNumber: `{{number}}은(는) 예약된 번호입니다. 다른 번호를 선택해 주세요.`,
    createFailed: `생성 실패`,
    updateFailed: `업데이트 실패`,
    breadcrumbAdd: `{{typeLabel}} 추가`,
    breadcrumbEdit: `{{typeLabel}} {{value}} 편집`,
    backToList: `← {{listLabel}}로 돌아가기`,
    wrongPbx: `이 내선은 선택한 PBX에 없습니다`,
    wrongPbxDescription: `이 내선은 선택한 PBX에 속하지 않습니다.`,
    openInPbx: `{{pbxName}}에서 열기`,
    loadFailed: `이 내선을 불러올 수 없습니다`,
    options: `옵션`,
    saveChanges: `변경 사항 저장`,
    omitFromDirectory: `디렉터리에서 제외`,
    nameRequired: `이름은 필수입니다`,
    numberRequired: `번호는 필수입니다`,
    extensionNumberPlaceholder: `내선 번호`,
    yes: `예`,
    no: `아니요`,
    members: `구성원`,
    membersNoun: `구성원`,
    userGroups: `사용자 그룹`,
    userGroupsNoun: `사용자 그룹`,
    holdMusic: `대기 음악`,
    line: {
      requiredFields: `이름, 번호, 위치 및 할당된 프로필이 필요합니다.`,
      selectLocation: `위치를 선택하세요`,
      notType: `회선이 아닙니다`,
      pbxUuid: `PBX UUID`,
      pbxName: `PBX`,
      sipSecret: `SIP 시크릿`,
      sipSecretReset: `SIP 시크릿이 재설정되었습니다`,
      reset: `재설정`,
      extensionUuid: `내선 UUID`,
      dialNow: `다이얼 나우`,
      dialNowCleared: `다이얼 나우가 지워졌습니다`,
      clear: `지우기`,
      assignedProfileMobileDevices: `할당된 프로필의 모바일 장치`,
      addTitle: `회선 추가`,
      title: `회선`,
      voicemailOptions: `음성사서함`,
      outDialExtension: `OutDial 내선`,
      outDialHelp: `OutDial 내선 도움말`,
      outDialHelpNo: `아니요: 이 내선은 일반 내선으로 작동합니다.`,
      outDialHelpYes: `예: 이 내선은 전달 번호로만 전달됩니다.`,
      outDialHelpBeAware: `유의 사항:`,
      outDialHelpWarningOne: `통화가 외부 번호로 전달된 후에는 시스템에서 통화를 다시 가져올 수 없습니다.`,
      outDialHelpWarningTwo: `OutDial 내선에서는 시스템 음성사서함을 사용할 수 없습니다.`,
      outDialEnabledHint: `OutDial이 활성화되어 있습니다. 통화는 전달 번호로 전달되며, 발신자 ID는 발신 통화 구간에 계속 적용됩니다.`,
      outDialDisabledHint: `OutDial이 비활성화되어 있습니다. 발신자 설정이 적용됩니다.`,
      loadingProfiles: `프로필을 불러오는 중…`,
      voicemailEnabled: `음성사서함 사용`,
      forwardingNumber: `전달 번호 (OutDial)`,
      localAreaCode: `로컬 지역 번호`,
      localAreaCodeInvalid: `지역 번호가 올바르지 않습니다. {{areaCode}}은(는) 로컬 지역 번호로 사용할 수 없습니다.`,
      localAreaCodeNotAllowed: `지역 번호 {{areaCode}}은(는) 로컬 지역 번호로 사용할 수 없습니다.`,
      outboundCallerIdRequired: `발신자 ID를 선택해 주세요.`,
      outboundCallerIdNoOptions: `이 PBX에 구성된 전화 번호가 없습니다. 이 회선을 편집하기 전에 PBX에 전화 번호를 추가하세요.`,
      outboundCallerIdMissingSavedValue: `이 회선에 유효한 발신자 ID가 없습니다. 계속하기 전에 하나를 선택하고 저장하세요.`,
      expandDetails: `세부 정보 표시`,
      collapseDetails: `세부 정보 숨기기`,
      activate: `활성화`,
      deactivate: `비활성화`,
      findMe: {
        title: `나를 찾기`,
        mode: `나를 찾기`,
        number: `나를 찾기 번호`,
        delay: `나를 찾기 시작 시간`,
        modeOptions: {
          '0': `끔`,
          '1': `켬`,
          '2': `전체 동시 울림`,
        },
        delayOptions: {
          '0': `즉시`,
          '5': `5초`,
          '10': `10초`,
          '15': `15초`,
        },
        numberRequired: `나를 찾기가 켜져 있으면 나를 찾기 번호를 입력해야 합니다.`,
        numberInvalid: `나를 찾기 번호는 숫자와 선택적인 선행 +만 사용할 수 있습니다(최대 15자리).`,
      },
      callRecording: `통화 녹음`,
      callRecordingOptions: {
        '0': `아니요`,
        '1': `예`,
        '2': `시스템 기본값`,
      },
      dedicatedFax: `전용 팩스`,
      inCallFeatureCodes: `통화 중 기능 코드 사용`,
      createButton: `회선 생성`,
      saveAndCreateAnother: `저장 후 새로 만들기`,
      details: `회선 세부 정보`,
      codecOpus: `느린 인터넷에 최적 (OPUS) 지원: 웹 전화, Yealink`,
      codecPcmu: `표준 (PCMU) 지원: 모든 전화 유형`,
      codecG722: `HD 오디오 (G722) 지원: Yealink, Cisco`,
      codecG729: `지원 중단 - 느린 인터넷에 적합 (G729) 지원: Yealink, Cisco`,
    },
    ringGroup: {
      ringing: `벨울림`,
      created: `링 그룹이 생성되었습니다`,
      updated: `링 그룹이 업데이트되었습니다`,
      saveFailed: `링 그룹을 저장하지 못했습니다`,
      notType: `링 그룹이 아닙니다`,
      notTypeDescription: `이 내선은 링 그룹이 아닙니다. 다른 유형은 내선 목록에서 편집하세요.`,
      namePlaceholder: `링 그룹 이름`,
      ringStrategy: `벨울림 전략`,
      ringAll: `전체 벨울림`,
      pagingGroup: `페이징 그룹`,
      ringbackAudio: `링백 오디오`,
      createButton: `링 그룹 생성`,
      updateButton: `링 그룹 업데이트`,
    },
    callQueue: {
      created: `콜 큐가 생성되었습니다`,
      updated: `콜 큐가 업데이트되었습니다`,
      saveFailed: `콜 큐를 저장하지 못했습니다`,
      notType: `콜 큐가 아닙니다`,
      queueSettings: `큐 설정`,
      ringStrategy: `벨울림 전략`,
      strategyRingAll: `전체 벨울림`,
      strategyRoundRobin: `라운드 로빈`,
      strategyLongestIdle: `최장 유휴`,
      strategyRandom: `무작위`,
      strategyLeastTalkTime: `최소 통화 시간`,
      strategyFewestCalls: `최소 통화 수`,
      preCallAnnouncement: `통화 전 안내`,
      maxNoAnswer: `최대 무응답 (시도 횟수)`,
      busyDelayTime: `통화 중 지연 시간 (초)`,
      rejectDelayTime: `거절 지연 시간 (초)`,
      wrapUpTime: `마무리 시간 (초)`,
      noAnswerDelayTime: `무응답 지연 시간 (초)`,
      maxRingDuration: `최대 벨울림 시간 (초)`,
      maxCallersInQueue: `큐의 최대 발신자 수`,
      maxWaitTime: `최대 대기 시간 (초)`,
      timeoutDestination: `시간 초과 대상`,
      escapeDestination: `이탈 대상`,
      destinationSearchPlaceholder: `내선 검색...`,
      destinationNoMatches: `검색과 일치하는 내선이 없습니다.`,
      destinationCurrentUnlisted: `{{value}} (현재)`,
      periodicAnnouncements: `주기적 안내`,
      positionAnnouncementFrequency: `순번 안내 빈도`,
      positionAnnouncementMessage: `순번 안내 메시지`,
      queuePosition: `큐 순번`,
      agentsNoun: `상담원`,
      newButton: `새 콜 큐`,
      updateButton: `콜 큐 업데이트`,
    },
    conferenceRoom: {
      moderatorPinValidation: `진행자 PIN은 정확히 4자리 숫자여야 합니다.`,
      conferencePinValidation: `회의 PIN은 정확히 4자리 숫자여야 합니다.`,
      holdMusicRequired: `대기 음악은 필수입니다.`,
      created: `회의실이 생성되었습니다`,
      updated: `회의실이 업데이트되었습니다`,
      saveFailed: `회의실을 저장하지 못했습니다`,
      notType: `회의실이 아닙니다`,
      moderatorRequired: `진행자 필요`,
      moderatorPin: `진행자 PIN`,
      userPinRequired: `사용자 PIN 필요`,
      conferencePin: `회의 PIN`,
      defaultMuted: `기본 음소거`,
      announceNewParticipants: `새 참가자 알림`,
      conferenceGreeting: `회의 인사말`,
      selectHoldMusic: `대기 음악 선택`,
      createButton: `회의실 생성`,
      updateButton: `회의실 업데이트`,
    },
    virtualFax: {
      invalidEmail: `유효한 이메일 주소를 입력하세요.`,
      passwordsDoNotMatch: `비밀번호가 일치하지 않습니다.`,
      passwordMinLength: `보안 팩스 비밀번호는 8자 이상이어야 합니다.`,
      created: `가상 팩스가 생성되었습니다`,
      updated: `가상 팩스가 업데이트되었습니다`,
      saveFailed: `가상 팩스를 저장하지 못했습니다`,
      secureFaxLinkResent: `보안 팩스 링크가 다시 전송되었습니다`,
      resendFailed: `재전송 실패`,
      resendSaveFailed: `보안 팩스 링크를 다시 전송하지 못했습니다`,
      notType: `가상 팩스가 아닙니다`,
      receivingOptions: `수신 옵션`,
      receivingEmailDestination: `수신 이메일 주소`,
      secureFax: `보안 팩스`,
      download: `다운로드`,
      singleDownload: `1회 다운로드`,
      download5: `5회 다운로드`,
      download10: `10회 다운로드`,
      download15: `15회 다운로드`,
      failedLoginRetries: `비밀번호 실패 허용 횟수`,
      retries5: `5회 재시도`,
      retries10: `10회 재시도`,
      retries15: `15회 재시도`,
      linkExpiresIn: `다운로드 링크 만료 기간`,
      day1: `1일`,
      day2: `2일`,
      day3: `3일`,
      day7: `7일`,
      day15: `15일`,
      day30: `30일`,
      changePasswordOptional: `보안 팩스 비밀번호 변경 (선택 사항)`,
      userGroupsDescription: `이 가상 팩스에 액세스할 수 있는 사용자 그룹을 선택하세요.`,
      sending: `전송 중…`,
      resendSecureFaxLink: `보안 팩스 링크 다시 전송`,
      createButton: `가상 팩스 기기 생성`,
      updateButton: `가상 팩스 기기 업데이트`,
    },
    shared: {
      generalOptions: `일반 옵션`,
      voicemailTimeZone: `음성사서함 시간대`,
      voicemailPassword: `음성사서함 비밀번호`,
      playVoicemailInstructions: `음성사서함 안내 재생`,
      notificationOptions: `알림 옵션`,
      sendEmailNotifications: `이메일 알림 보내기`,
      messageEmailAddress: `메시지 이메일 주소`,
      emailPlaceholder: `email@example.com`,
      attachVoicemailMessage: `음성사서함 메시지 첨부`,
      deleteMessageAfterSend: `전송 후 메시지 삭제`,
      outgoingMessage: `발신 메시지`,
      useSystemGreetings: `시스템 인사말 사용`,
      customGreeting: `사용자 지정 인사말`,
    },
    sharedVoicemail: {
      created: `공유 음성사서함이 생성되었습니다`,
      updated: `공유 음성사서함이 업데이트되었습니다`,
      saveFailed: `공유 음성사서함을 저장하지 못했습니다`,
      notType: `공유 음성사서함이 아닙니다`,
      userGroupsDescription: `이 공유 음성사서함에 액세스할 수 있는 사용자 그룹을 선택하세요.`,
      createButton: `공유 음성사서함 생성`,
      updateButton: `공유 음성사서함 업데이트`,
    },
  },

  updateLocation: {
    title: `위치 업데이트`,
    description: `{{count}}개 회선의 위치를 업데이트하시겠습니까?`,
    confirm: `위치 업데이트`,
  },

  pickList: {
    assignedWithLimit: `할당된 {{itemNoun}}: {{count}} / {{max}}`,
    assigned: `할당된 {{itemNoun}}: {{count}}`,
    noneAssigned: `아직 추가된 {{itemNoun}}이(가) 없습니다.`,
    remove: `제거`,
    available: `사용 가능한 {{itemNoun}}: {{count}}`,
    searchAvailable: `사용 가능한 {{itemNoun}} 검색`,
    maxReached: `최대 {{max}}개의 {{itemNoun}}에 도달했습니다.`,
    noneAvailable: `사용 가능한 {{itemNoun}}이(가) 없습니다.`,
  },

  localPresence: {
    0: `끔`,
    1: `그룹 1`,
    2: `그룹 2`,
    3: `그룹 3`,
    4: `그룹 4`,
    5: `그룹 5`,
    6: `그룹 6`,
  },

  codecOptions: {
    opus: `느린 인터넷에 최적 (OPUS) — 지원: 웹 전화, Yealink`,
    pcmu: `표준 (PCMU) — 지원: 모든 전화 유형`,
    g722: `HD 오디오 (G722) — 지원: Yealink, Cisco`,
    g729: `지원 중단 — 느린 인터넷에 적합 (G729) — 지원: Yealink, Cisco`,
    default: `시스템 기본값`,
  },

  timezones: {
    america_new_york: `Eastern Time`,
    america_chicago: `Central Time`,
    america_denver: `Mountain Time`,
    america_los_angeles: `Pacific Time`,
    america_phoenix: `Arizona`,
    america_anchorage: `Alaska Time`,
    pacific_honolulu: `Hawaii Time`,
    america_toronto: `Toronto (Eastern)`,
    america_vancouver: `태평양 시간 - 브리티시컬럼비아 (PCT)`,
    europe_london: `London (GMT/BST)`,
    europe_paris: `Paris (CET)`,
    australia_sydney: `Sydney (AEST)`,
    utc: `UTC`,
  },

  // ---------------------------------------------------------------------------
  // Create extension options
  // ---------------------------------------------------------------------------
  createOptions: {
    add: `추가`,
    addLine: `회선 추가`,
    addRingGroup: `링 그룹 추가`,
    addCallQueue: `콜 큐 추가`,
    addConferenceRoom: `회의실 추가`,
    addVirtualFax: `가상 팩스 추가`,
    addSharedVoicemail: `공유 음성사서함 추가`,
  },

  // ---------------------------------------------------------------------------
  // Ordering options
  // ---------------------------------------------------------------------------
  ordering: {
    lastUpdatedNewest: `최근 수정 (최신순)`,
    lastUpdatedOldest: `최근 수정 (오래된순)`,
    createdNewest: `생성일 (최신순)`,
    createdOldest: `생성일 (오래된순)`,
    nameAZ: `이름 (A\u2013Z)`,
    nameZA: `이름 (Z\u2013A)`,
    numberAsc: `번호 (오름차순)`,
    numberDesc: `번호 (내림차순)`,
  },

  // ---------------------------------------------------------------------------
  // Slug-to-label mappings (for breadcrumbs)
  // ---------------------------------------------------------------------------
  slugLabels: {
    line: `회선`,
    ringGroup: `링 그룹`,
    callQueue: `콜 큐`,
    conferenceRoom: `회의실`,
    virtualFax: `가상 팩스`,
    sharedVoicemail: `공유 음성사서함`,
  },

  // ---------------------------------------------------------------------------
  // List labels for type slugs
  // ---------------------------------------------------------------------------
  listLabels: {
    lines: `회선`,
    ringGroups: `링 그룹`,
    callQueues: `콜 큐`,
    conferenceRooms: `회의실`,
    virtualFax: `가상 팩스`,
    sharedVoicemail: `공유 음성사서함`,
  },
} satisfies ExtensionsLocale
